import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Modal,
  ModalBody,
  Spinner,
  FormGroup,
  Label,
  Input,
  FormFeedback
} from "reactstrap";
import { Icon } from "../../../../components/Component";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import { createBlogKeyword, updateBlogKeyword } from "../../../../api/blogs/keywords";

const AddKeywordModal = ({ isOpen, toggle, onSuccess, editData = null }) => {
  const axios = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!editData;

  // Form state
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (editData) {
      setName(editData.name || "");
    } else {
      setName("");
    }
    setError("");
    setTouched(false);
  }, [editData, isOpen]);

  // Handle input change
  const handleNameChange = (e) => {
    setName(e.target.value);
    validateName(e.target.value);
    setTouched(true);
  };

  // Validate name field
  const validateName = (value) => {
    if (!value.trim()) {
      setError("Keyword name is required");
      return false;
    } else if (value.trim().length < 2) {
      setError("Keyword name must be at least 2 characters");
      return false;
    } else {
      setError("");
      return true;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate the name field
    if (!validateName(name)) {
      setTouched(true);
      return;
    }

    const trimmedName = name.trim();
    const payload = { name: trimmedName };

    setLoading(true);
    try {
      if (isEditMode) {
        await updateBlogKeyword(axios, editData.id, payload);
        toast.success("Keyword updated successfully");
      } else {
        await createBlogKeyword(axios, payload);
        toast.success("Keyword created successfully");
      }
      onSuccess();
      toggle();
      // Reset form
      setName("");
      setError("");
      setTouched(false);
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes("duplicate")) {
        setError("This keyword already exists. Please use a different name.");
      } else {
        toast.error(err.message || "An error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="modal-dialog-centered">
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            toggle();
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <div className="p-2">
          <h5 className="title">{isEditMode ? "Edit Keyword" : "Add New Keyword"}</h5>
          <div className="mt-4">
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="name" className="form-label">
                  Keyword Name <span className="text-danger">*</span>
                </Label>
                <div className="form-control-wrap">
                  <Input
                    type="text"
                    className={`form-control ${touched && error ? "is-invalid" : ""}`}
                    id="name"
                    name="name"
                    value={name}
                    onChange={handleNameChange}
                    onBlur={() => setTouched(true)}
                    placeholder="Enter keyword name"
                    disabled={loading}
                  />
                  {touched && error && (
                    <FormFeedback className="d-block">{error}</FormFeedback>
                  )}
                </div>
              </FormGroup>
              <div className="form-group mt-4 d-flex justify-content-end">
                <Button type="button" color="secondary" className="me-2" onClick={toggle} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={loading}>
                  {loading ? <Spinner size="sm" /> : isEditMode ? "Update" : "Add"}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AddKeywordModal;
